import { Avatar, Css } from "@homebound/beam";
import { sentenceCase } from "change-case";
import { useNavigate } from "react-router-dom";
import { CommentCountBubble, formatDate } from "src/components";
import { JobLogDetailFragment, JobLogNoteDetailFragment } from "src/fragments/circularFragments.generated";
import { useRouteParams } from "src/hooks/useRouteParams";
import { createDevelopmentJobLogNoteDrawerUrl, createProjectJobLogNoteDrawerUrl } from "src/RouteUrls";
import { nonEmpty } from "src/utils";
import { formatMonthDay } from "src/utils/dates";
import { JobLogImages } from "./JobLogImages";
import { JobLogNoteTags } from "./JobLogNoteTags";

type JobLogNoteProps = { note: JobLogNoteDetailFragment; jobLog: JobLogDetailFragment };

export function JobLogNote(props: JobLogNoteProps) {
  const { note, jobLog } = props;
  const { id, content, createdAt, internalUser, type, title } = note;
  const formattedDate = formatMonthDay(createdAt.toPlainDate());
  const formattedTime = formatDate(createdAt, "none", "short");
  const detailsPhrase = `${internalUser.name}, ${formattedDate} at ${formattedTime}`;
  const { developmentId } = useRouteParams<{ developmentId: string }>();
  const navigate = useNavigate();
  const isProject = !developmentId;
  function onNoteClick() {
    if (isProject) {
      navigate(createProjectJobLogNoteDrawerUrl(jobLog.project.id, jobLog.id, id));
    } else {
      navigate(createDevelopmentJobLogNoteDrawerUrl(developmentId, jobLog.id, id));
    }
  }

  return (
    <div
      data-testid="jobLogNote"
      css={
        Css.bgWhite.bshBasic
          .borderRadius("8px")
          .bw1.bss.bcGray300.w100.p3.cursorPointer.add("transition", "visibility 100ms").$
      }
      key={id}
      onClick={onNoteClick}
    >
      <div css={Css.df.fdc.$}>
        <div css={Css.gray900.mb2.pl4.md.$}>
          {" "}
          <b>{title ?? sentenceCase(type ?? "")}</b>
        </div>
        <JobLogNoteTags note={note} />
        <div css={Css.gray900.xs.pl4.mb1.$} dangerouslySetInnerHTML={{ __html: formatJobLogNote(content) }}></div>
        <div css={Css.dn.if(nonEmpty(note.jobLogImages!)).mb1.db.pl4.$}>
          <JobLogImages
            parentId={note.id}
            description={note.content}
            images={note.jobLogImages!}
            display={"horizontal"}
          />
        </div>
        <div css={Css.df.fdr.jcsb.mt1.$}>
          <div css={Css.df.$}>
            <Avatar src={internalUser.avatar} name={internalUser.name} size="sm" />
            <div css={Css.df.fdc.jcc.mr1.$}>
              <span css={Css.gray700.xs.pl1.mtPx(4).mbPx(4).$}>{detailsPhrase}</span>
            </div>
          </div>
          <CommentCountBubble streams={note.streams} />
        </div>
      </div>
    </div>
  );
}

// Converts mixed markdown+HTML job log note content to renderable HTML.
// Markdown patterns from the mobile app (**, ~~, *, -) coexist with HTML tags from the Trix web editor.
export function formatJobLogNote(content: string) {
  return content
    .replace(/&amp;quot;/g, '"')
    .replace(/&amp;#39;/g, "'")
    .replace(/~~(.*?)~~/g, "<s>$1</s>") // strikethrough before bold to avoid conflict
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>") // italic after bold to avoid double-asterisk conflict
    .replace(/(^|<br>)- (.*?)(?=<br>|$)/g, "$1<li>$2</li>") // only match - at line starts
    .replace(/<\/li><br><li>/g, "</li><li>"); // strip <br> between adjacent list items
}
